
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { safeLocalStorage } from '@/utils/safeStorage';
import { appliquerConsentement } from '@/lib/mesureAudience';

// Version de la politique cookies (à incrémenter en cas de changement majeur)
const COOKIE_POLICY_VERSION = '1.0';

// Durée de validité du consentement en jours (13 mois max selon CNIL)
const CONSENT_VALIDITY_DAYS = 395; // ~13 mois

// Types pour les préférences de cookies
export type CookiePreferences = {
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  necessary: boolean;
};

// Type pour les données de consentement stockées
type ConsentData = {
  preferences: CookiePreferences;
  consentDate: string;
  policyVersion: string;
};

// Valeurs par défaut
const defaultPreferences: CookiePreferences = {
  necessary: true, // Toujours obligatoire
  analytics: false,
  functional: false,
  marketing: false,
};

// Hook pour gérer les préférences de cookies
export const useCookieConsentLogic = () => {
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState<boolean>(false);

  // Vérifier si le consentement a expiré (13 mois max)
  const isConsentExpired = useCallback((consentDate: string): boolean => {
    const consentTime = new Date(consentDate).getTime();
    const now = Date.now();
    const maxValidityMs = CONSENT_VALIDITY_DAYS * 24 * 60 * 60 * 1000;
    return now - consentTime > maxValidityMs;
  }, []);

  // Vérifier si la version de la politique a changé
  const isPolicyVersionChanged = useCallback((storedVersion: string): boolean => {
    return storedVersion !== COOKIE_POLICY_VERSION;
  }, []);

  // Charger les préférences depuis le localStorage
  useEffect(() => {
    // safeLocalStorage plutôt que localStorage : en navigation privée ou stockage
    // bloqué, l'accès direct LÈVE une exception. Elle remontait jusqu'à
    // handleAcceptAll, dont le setIsOpen(false) n'était donc jamais atteint : le
    // bandeau restait ouvert et paraissait ne pas réagir. Le wrapper retombe sur un
    // stockage mémoire — le choix vaut au moins pour la session en cours.
    const storedData = safeLocalStorage.getItem('cookie-consent-data');

    if (storedData) {
      try {
        const consentData: ConsentData = JSON.parse(storedData);
        
        // Vérifier l'expiration ou le changement de version
        if (isConsentExpired(consentData.consentDate) || isPolicyVersionChanged(consentData.policyVersion)) {
          // Réinitialiser le consentement
          safeLocalStorage.removeItem('cookie-consent-data');
          setPreferences(defaultPreferences);
          setHasConsented(false);
          return;
        }

        setPreferences(consentData.preferences);
        setHasConsented(true);
        
        // Appliquer les préférences au chargement
        applyPreferences(consentData.preferences, true);
      } catch (e) {
        console.error('Erreur lors du chargement des préférences de cookies:', e);
        setPreferences(defaultPreferences);
        setHasConsented(false);
      }
    }
  }, [isConsentExpired, isPolicyVersionChanged]);

  // Appliquer les préférences : consentement Google et chargement de GTM au premier
  // accord (lib/mesureAudience). Jusqu'au 23/10/2026, cette mise à jour partait avant
  // que `gtag` existe, et GTM était chargé pour tout le monde, refus compris.
  const applyPreferences = (prefs: CookiePreferences, aLOuverture = false) => {
    appliquerConsentement(prefs, aLOuverture);
  };

  // Sauvegarder les préférences avec horodatage
  const savePreferences = (newPreferences: CookiePreferences) => {
    // Nécessaire est toujours true
    const updatedPreferences = { ...newPreferences, necessary: true };
    
    const consentData: ConsentData = {
      preferences: updatedPreferences,
      consentDate: new Date().toISOString(),
      policyVersion: COOKIE_POLICY_VERSION,
    };

    safeLocalStorage.setItem('cookie-consent-data', JSON.stringify(consentData));
    
    // Migration : supprimer les anciennes clés si elles existent
    safeLocalStorage.removeItem('cookie-preferences');
    safeLocalStorage.removeItem('cookie-consent');
    
    setPreferences(updatedPreferences);
    setHasConsented(true);

    // Appliquer les préférences (activer/désactiver les cookies)
    applyPreferences(updatedPreferences);

    return updatedPreferences;
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    savePreferences(allAccepted);
    toast.success('Tous les cookies ont été acceptés', { duration: 3000 });
  };

  const acceptNecessary = () => {
    savePreferences(defaultPreferences);
    toast.success('Seuls les cookies nécessaires ont été acceptés', { duration: 3000 });
  };

  const resetConsent = () => {
    safeLocalStorage.removeItem('cookie-consent-data');
    safeLocalStorage.removeItem('cookie-preferences');
    safeLocalStorage.removeItem('cookie-consent');
    setPreferences(defaultPreferences);
    setHasConsented(false);
    
    // Révoquer le consentement
    applyPreferences(defaultPreferences);
  };

  // Fonction pour ouvrir les préférences (utilisée par le bouton flottant)
  const openPreferences = useCallback(() => {
    setHasConsented(false);
  }, []);

  return {
    preferences,
    setPreferences,
    hasConsented,
    savePreferences,
    acceptAll,
    acceptNecessary,
    resetConsent,
    openPreferences,
  };
};
